import { logLevels } from "~/logging/levels";
import { injectRequired } from "~/injectRequired";
import { LogFunctionKey } from "~/injectionKeys";
import { type SerializableErrorInfo, serializeError } from "~/errors/util";

/**
 * Get a handler for when the user clicks the "Report" button on an error message.
 *
 * This uses `inject`, so it's only meant to be used in a Vue component context.
 */
export function useReport(): (
  e: Error | SerializableErrorInfo | undefined,
) => void {
  const log = injectRequired(LogFunctionKey);

  /**
   * A handler for when the user clicks the "Report" button on an error message.
   * @param e
   *   The error to report.
   *   In some cases, this may already be serialized, or not,
   *   so it can be an `Error` object or a `SerializableErrorInfo` object.
   *   If it's undefined, no action is taken.
   */
  function report(e: Error | SerializableErrorInfo | undefined): void {
    if (!e) return;

    const errorObject = e instanceof Error ? e : undefined;
    const serializedErrorInfo = e instanceof Error ? serializeError(e) : e;

    log({
      level: logLevels.error,
      message: e.message,
      error: errorObject,
      extra: { ...serializedErrorInfo, reportedManually: true },
    });
  }

  return report;
}
